/**
 * useGameStore — live match state for the React UI layer.
 *
 * WHY NOT persisted: match state is ephemeral; persisting it would cause the game
 * to "resume" mid-match after a page reload, which is not a supported feature.
 * Persistent data (XP, gold, decks) lives in useProgressionStore.
 *
 * WHY setEventBus exists: the EventBus is created inside MatchEngine (engine layer)
 * and passed up to MatchScene (game layer), which calls setEventBus() to let this
 * store subscribe to GOLD_CHANGED / HP_CHANGED / MATCH_END. The store cannot
 * import from the engine layer directly (would violate the 6-layer dependency rule
 * by making State depend on Game Logic at import time rather than at runtime).
 */
import { create } from 'zustand'
import type { EventBus } from '@engine/EventBus'
import { CampaignSystem } from '@engine/CampaignSystem.js'
import { useNavigationStore } from './useNavigationStore.js'
import type { CampaignMissionDef } from '@interfaces/index.js'
import campaignData from '@data/campaign.json'
import { AnalyticsService } from '../services/AnalyticsService.js'
import { ReplayRecorder } from '../services/ReplayRecorder.js'
import { ReplayService } from '../services/ReplayService.js'

const _campaignMissions = campaignData as CampaignMissionDef[]

export interface PerPlayerStats {
  totalDamage: number
  towerDamage: Record<string, number>
  goldEarned: number
  unitsSent: number
  unitsSentMap: Record<string, number>
  towersBuilt: number
}

export interface MatchStats {
  player: PerPlayerStats
  opponent: PerPlayerStats
  matchDurationMs: number
}

interface GameState {
  gold: number
  incomePerSec: number
  hp: number
  opponentHp: number
  remainingMs: number
  phase: 'idle' | 'running' | 'paused' | 'ended'
  matchOutcome: 'win' | 'lose' | 'draw' | null
  localPlayerId: string
  winnerName: string | null
  matchStats: MatchStats | null
  surrenderedByLocalPlayer: boolean
  /** Points delta from the last completed match (e.g. +30, -10, -20). */
  pointsDelta: number | null
  /** ELO of the opponent at match start — used to compute ELO delta when recording the result. */
  opponentLeaguePoints: number | null
  /** Coop mode: HP of the human partner (updated via WebSocket relay). -1 = not in coop mode. */
  coopPartnerHp: number
  // loading state
  isLoading: boolean
  loadingProgress: number
  loadingMessage: string
  lastError: string | null
  // actions
  setGold: (gold: number, income: number) => void
  setHp: (hp: number) => void
  setOpponentHp: (hp: number) => void
  setRemainingMs: (ms: number) => void
  setPhase: (phase: GameState['phase']) => void
  setMatchOutcome: (outcome: GameState['matchOutcome']) => void
  setLocalPlayer: (id: string) => void
  setWinnerName: (name: string | null) => void
  setMatchStats: (stats: MatchStats) => void
  setPointsDelta: (delta: number | null) => void
  setOpponentLeaguePoints: (elo: number | null) => void
  setCoopPartnerHp: (hp: number) => void
  resetMatchState: (startHp: number) => void
  // loading actions
  setLoading: (progress: number, message: string) => void
  setLoadingDone: () => void
  setError: (message: string) => void
  clearError: () => void
}

export const useGameStore = create<GameState>((set) => ({
  gold: 200,
  incomePerSec: 2,
  hp: 20,
  opponentHp: 20,
  remainingMs: 900000,
  phase: 'idle',
  matchOutcome: null,
  localPlayerId: 'player1',
  winnerName: null,
  matchStats: null,
  surrenderedByLocalPlayer: false,
  pointsDelta: null,
  opponentLeaguePoints: null,
  coopPartnerHp: -1,
  // loading state defaults
  isLoading: false,
  loadingProgress: 0,
  loadingMessage: 'Lade...',
  lastError: null,

  setGold: (gold, income) => set({ gold, incomePerSec: income }),
  setHp: (hp) => set({ hp }),
  setOpponentHp: (opponentHp) => set({ opponentHp }),
  setRemainingMs: (remainingMs) => set({ remainingMs }),
  setPhase: (phase) => set({ phase }),
  setMatchOutcome: (matchOutcome) => set({ matchOutcome }),
  setLocalPlayer: (localPlayerId) => set({ localPlayerId }),
  setWinnerName: (winnerName) => set({ winnerName }),
  setMatchStats: (matchStats) => set({ matchStats }),
  setPointsDelta: (pointsDelta) => set({ pointsDelta }),
  setOpponentLeaguePoints: (opponentLeaguePoints) => set({ opponentLeaguePoints }),
  setCoopPartnerHp: (coopPartnerHp) => set({ coopPartnerHp }),
  resetMatchState: (startHp) => set({
    gold: 200,
    incomePerSec: 2,
    hp: startHp,
    opponentHp: startHp,
    remainingMs: 900000,
    phase: 'running',
    matchOutcome: null,
    winnerName: null,
    matchStats: null,
    surrenderedByLocalPlayer: false,
    pointsDelta: null,
    opponentLeaguePoints: null,
    coopPartnerHp: -1,
  }),
  // loading actions — setLoading also activates the overlay (isLoading: true)
  setLoading: (progress, message) => set({ isLoading: true, loadingProgress: progress, loadingMessage: message }),
  setLoadingDone: () => set({ isLoading: false, loadingProgress: 100 }),
  setError: (message) => set({ lastError: message }),
  clearError: () => set({ lastError: null }),
}))

let _currentBus: EventBus | null = null

export function setEventBus(bus: EventBus, startHp = 20, localPlayerId = 'player1'): void {
  if (_currentBus !== null) {
    _currentBus.off()
  }
  _currentBus = bus

  // Reset match state so HP/gold always start clean for every new match (#11)
  useGameStore.getState().resetMatchState(startHp)
  // Ensure the store knows which player is local (critical for player2 in PvP)
  useGameStore.getState().setLocalPlayer(localPlayerId)

  // Start replay recording — snapshot every 5 s via ReplayRecorder
  const _matchTimestamp = new Date().toISOString()
  ReplayRecorder.getInstance().startRecording(_matchTimestamp, () => {
    const st = useGameStore.getState()
    return {
      gold: st.gold,
      hp: st.hp,
      oppHp: st.opponentHp,
      towerCount: 0, // towerCount is maintained by MatchScene; 0 is a safe default here
    }
  })

  // Campaign objective tracking — wired here so MatchScene doesn't need to know about it
  const { gameMode, selectedMissionIndex } = useNavigationStore.getState()
  if (gameMode === 'campaign' && selectedMissionIndex !== null) {
    const mission = _campaignMissions[selectedMissionIndex]
    if (mission) {
      const { localPlayerId } = useGameStore.getState()
      CampaignSystem.getInstance().startMission(mission, bus, localPlayerId)
    }
  }

  // Replay — key event recording
  const recorder = ReplayRecorder.getInstance()
  bus.on('TOWER_PLACED', (e) => {
    const { localPlayerId: lpid } = useGameStore.getState()
    if (e.playerId === lpid) {
      const towerId = (e.payload as Record<string, unknown>)['towerId'] as string | undefined
      recorder.recordEvent('TOWER_PLACED', towerId !== undefined ? `Turm gesetzt: ${towerId}` : 'Turm gesetzt')
    }
  })
  bus.on('TOWER_UPGRADED', (e) => {
    const { localPlayerId: lpid } = useGameStore.getState()
    if (e.playerId === lpid) {
      const instanceId = (e.payload as Record<string, unknown>)['instanceId'] as string | undefined
      recorder.recordEvent('TOWER_UPGRADED', instanceId !== undefined ? `Turm upgradet: ${instanceId}` : 'Turm upgradet')
    }
  })
  bus.on('TOWER_DEMOLISHED', (e) => {
    const { localPlayerId: lpid } = useGameStore.getState()
    if (e.playerId === lpid) recorder.recordEvent('TOWER_DEMOLISHED', 'Turm abgerissen')
  })
  bus.on('UNIT_SENT', (e) => {
    const { localPlayerId: lpid } = useGameStore.getState()
    if (e.playerId === lpid) {
      const unitId = (e.payload as Record<string, unknown>)['unitId'] as string | undefined
      recorder.recordEvent('UNIT_SENT', unitId !== undefined ? `Einheit gesendet: ${unitId}` : 'Einheit gesendet')
    }
  })
  bus.on('UNIT_KILLED', () => {
    recorder.recordEvent('UNIT_KILLED', 'Einheit getötet')
  })
  bus.on('BASE_UPGRADED', (e) => {
    const { localPlayerId: lpid } = useGameStore.getState()
    if (e.playerId === lpid) recorder.recordEvent('BASE_UPGRADED', 'Basis upgradet')
  })
  bus.on('SPELL_CAST', (e) => {
    const { localPlayerId: lpid } = useGameStore.getState()
    if (e.playerId === lpid) recorder.recordEvent('SPELL_CAST', 'Zauber gewirkt')
  })

  bus.on('GOLD_CHANGED', (e) => {
    const { localPlayerId } = useGameStore.getState()
    if (e.playerId === localPlayerId) {
      useGameStore.getState().setGold(
        e.payload['newTotal'] as number,
        e.payload['newIncome'] as number
      )
    }
  })

  bus.on('HP_CHANGED', (e) => {
    const { localPlayerId } = useGameStore.getState()
    if (e.playerId === localPlayerId) {
      useGameStore.getState().setHp(e.payload['newHp'] as number)
    } else {
      useGameStore.getState().setOpponentHp(e.payload['newHp'] as number)
    }
  })

  bus.on('MATCH_END', (e) => {
    const outcome = e.payload['outcome'] as 'win' | 'lose' | 'draw'
    const winner = e.payload['winner'] as string | null | undefined
    const reason = e.payload['reason'] as string | undefined
    const rawStats = e.payload['stats'] as Record<string, unknown> | undefined
    const durationSecs = e.payload['durationSecs'] as number | undefined
    const pointsDelta = e.payload['pointsDelta'] as number | undefined
    const { localPlayerId } = useGameStore.getState()
    const loser = e.payload['loser'] as string | undefined
    const surrendered = reason === 'quit' && loser === localPlayerId
    useGameStore.getState().setMatchOutcome(outcome)
    useGameStore.getState().setPhase('ended')
    useGameStore.getState().setWinnerName(winner ?? null)
    useGameStore.getState().setPointsDelta(pointsDelta ?? null)
    if (surrendered) {
      useGameStore.setState({ surrenderedByLocalPlayer: true })
    }
    if (rawStats !== undefined) {
      const perPlayerStats = rawStats['playerStats'] as Record<string, Record<string, unknown>> | undefined
      const localStats = perPlayerStats?.[localPlayerId]
      const opponentId = Object.keys(perPlayerStats ?? {}).find((id) => id !== localPlayerId)
      const opponentStats = opponentId !== undefined ? perPlayerStats?.[opponentId] : undefined

      useGameStore.getState().setMatchStats({
        player: {
          totalDamage: (localStats?.['totalDamage'] as number) ?? 0,
          towerDamage: (localStats?.['towerDamage'] as Record<string, number>) ?? {},
          goldEarned: (localStats?.['goldEarned'] as number) ?? 0,
          unitsSent: (localStats?.['unitsSent'] as number) ?? 0,
          unitsSentMap: (localStats?.['unitsSentMap'] as Record<string, number>) ?? {},
          towersBuilt: (localStats?.['towersBuilt'] as number) ?? 0,
        },
        opponent: {
          totalDamage: (opponentStats?.['totalDamage'] as number) ?? 0,
          towerDamage: (opponentStats?.['towerDamage'] as Record<string, number>) ?? {},
          goldEarned: (opponentStats?.['goldEarned'] as number) ?? 0,
          unitsSent: (opponentStats?.['unitsSent'] as number) ?? 0,
          unitsSentMap: (opponentStats?.['unitsSentMap'] as Record<string, number>) ?? {},
          towersBuilt: (opponentStats?.['towersBuilt'] as number) ?? 0,
        },
        matchDurationMs: (durationSecs ?? 0) * 1000,
      })

      const goldEarned = (localStats?.['goldEarned'] as number) ?? 0
      const towersBuilt = (localStats?.['towersBuilt'] as number) ?? 0
      const unitsSent = (localStats?.['unitsSent'] as number) ?? 0
      AnalyticsService.getInstance().logMatchEnd({
        outcome,
        durationSecs: durationSecs ?? 0,
        mode: useNavigationStore.getState().gameMode,
        goldEarned,
        towersBuilt,
        unitsSent,
      })
    }

    // Stop replay recording and persist the data
    const replayData = ReplayRecorder.getInstance().stopRecording()
    if (replayData !== null) {
      const fakeResult = {
        id: _matchTimestamp,
        timestamp: _matchTimestamp,
        mode: useNavigationStore.getState().gameMode as import('@interfaces/index.js').GameMode,
        mapId: '',
        durationSecs: Math.floor(((durationSecs ?? 0))),
        outcome,
        reason: 'defeat' as const,
        stats: {
          totalDamage: 0,
          towerDamage: {},
          goldEarned: 0,
          killGold: 0,
          unitsSent: 0,
          towersBuilt: 0,
          towersUpgraded: 0,
          spellsCast: 0,
        },
      }
      ReplayService.getInstance().saveReplay(fakeResult, replayData).catch(() => { /* ignore */ })
    }
  })
}

export function tickRemainingMs(remainingMs: number): void {
  useGameStore.getState().setRemainingMs(remainingMs)
}
